// This file is part of MinIO Operator
// Copyright (c) 2022 MinIO, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { createAsyncThunk } from "@reduxjs/toolkit";
import { getDefaultAffinity, getNodeSelector } from "../../../utils";
import { resetEditPoolForm } from "../editPoolSlice";
import { getTenantAsync } from "../../../tenantDetailsAsync";
import { RootState } from "../../../../../../app/store";
import { oprRpc } from "../../../../../../api";
import { ErrorResponseHandler } from "../../../../../../common/types";
import { setErrorSnackBarMessage } from "../../../../../systemSlice";
import { generatePoolName } from "../../../../utils/utils";
import {
  MinioOpPool,
  OperatorRpcsUpdatePoolBody,
  V1PodSecurityContext,
} from "../../../../../../api/operatorApi";
import { AsyncResourceRequest } from "../../../../../../common/common.types";

export const editPoolAsync = createAsyncThunk(
  "editPool/editPoolAsync",
  async (
    req: AsyncResourceRequest,
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;

    const tenant = state.tenants.tenantInfo;
    const selectedPool = state.tenants.selectedPool;
    const selectedStorageClass = state.editPool.fields.setup.storageClass;
    const numberOfNodes = state.editPool.fields.setup.numberOfNodes;
    const volumeSize = state.editPool.fields.setup.volumeSize;
    const volumesPerServer = state.editPool.fields.setup.volumesPerServer;
    const affinityType = state.editPool.fields.affinity.podAffinity;
    const nodeSelectorLabels =
      state.editPool.fields.affinity.nodeSelectorLabels;
    const withPodAntiAffinity =
      state.editPool.fields.affinity.withPodAntiAffinity;
    const tolerations = state.editPool.fields.tolerations;
    const securityContextEnabled =
      state.editPool.fields.configuration.securityContextEnabled;
    const securityContext = state.editPool.fields.configuration.securityContext;
    const customRuntime = state.editPool.fields.configuration.customRuntime;
    const runtimeClassName =
      state.editPool.fields.configuration.runtimeClassName;

    if (!tenant) {
      return;
    }

    const poolName = generatePoolName(tenant!.pools!);

    let affinityObject = {};

    switch (affinityType) {
      case "default":
        affinityObject = {
          affinity: getDefaultAffinity(tenant.name!, poolName),
        };
        break;
      case "nodeSelector":
        affinityObject = {
          affinity: getNodeSelector(
            nodeSelectorLabels,
            withPodAntiAffinity,
            tenant.name!,
            poolName,
          ),
        };
        break;
    }

    const tolerationValues = tolerations.filter(
      (toleration) => toleration.key?.trim() !== "",
    );

    const cleanPools = tenant?.pools
      ?.filter((pool) => pool.name !== selectedPool)
      .map((pool) => {
        let securityContextOption: V1PodSecurityContext | null = null;

        if (pool.securityContext) {
          if (
            !!pool.securityContext.runAsUser ||
            !!pool.securityContext.runAsGroup ||
            !!pool.securityContext.fsGroup
          ) {
            securityContextOption = { ...pool.securityContext };
          }
        }

        const request = pool;
        if (securityContextOption) {
          request.securityContext = securityContextOption!;
        }

        return request;
      }) as MinioOpPool[];

    let runtimeClass = {};

    if (customRuntime) {
      runtimeClass = {
        runtimeClassName,
      };
    }

    cleanPools.push({
      name: selectedPool || poolName,
      servers: numberOfNodes,
      volumesPerServer: volumesPerServer,
      volumeConfiguration: {
        size: `${volumeSize * 1073741824}`,
        storageClassName: selectedStorageClass,
        labels: undefined,
      },
      tolerations: tolerationValues,
      securityContext: securityContextEnabled ? securityContext : undefined,
      ...affinityObject,
      ...runtimeClass,
    });

    const data: OperatorRpcsUpdatePoolBody = {
      pools: cleanPools,
    };
    const poolsURL: string = `/namespaces/${tenant?.namespace || ""}/tenants/${
      tenant?.name || ""
    }/pools`;

    return oprRpc.api
      .updatePool(req.siteId, req.namespace, req.resourceId, data)
      .then(() => {
        dispatch(resetEditPoolForm());
        dispatch(getTenantAsync(req));
        return poolsURL;
      })
      .catch((err: ErrorResponseHandler) => {
        dispatch(setErrorSnackBarMessage(err));
      });
  },
);
